/**
 * GrannaventyretFlyer — printable A6 landscape flyer (148 × 105 mm)
 *
 * Props:
 *   qrDataUrl  string   Base64 data-URL produced by qrcode.toDataURL()
 *                       with color { dark: '#000000', light: '#ffffff' }
 *
 * The component injects its own @page rule so you can call window.print()
 * directly from a page that only renders this component.
 *
 * Print notes:
 *   - All coloured elements carry print-color-adjust: exact
 *   - Avoids large solid ink areas — safe for cheap home inkjet
 *   - Two copies placed side-by-side fill one A4 sheet at 100 % scale
 */

import React from 'react'

/* ─── @page + print reset ─────────────────────────────────────────── */
const PRINT_CSS = `
  @page {
    size: 148mm 105mm landscape;
    margin: 0 !important;
  }
  @media print {
    html, body {
      width:    148mm !important;
      height:   105mm !important;
      margin:   0     !important;
      padding:  0     !important;
      overflow: hidden !important;
      background: #fff !important;
    }
    .ga-no-print { display: none !important; }
    * {
      -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
    }
    /* Ensure flyer root fills page exactly — no rounded corners on outer edge */
    .ga-flyer-root {
      width:         148mm !important;
      height:        105mm !important;
      overflow:      hidden !important;
      border-radius: 0 !important;
      box-shadow:    none !important;
      position:      fixed !important;
      top:           0 !important;
      left:          0 !important;
    }
    /* Do NOT override border-radius on inner elements — they use inline styles */
  }
`

/* ─── Brand palette ────────────────────────────────────────────────── */
const C = {
  indigo:     '#4f46e5',
  violet:     '#7c3aed',
  indigoDark: '#1e1b4b',
  indigo50:   '#eef2ff',
  violet50:   '#f5f3ff',
  amber:      '#f59e0b',
  amberLight: '#fbbf24',
  white:      '#ffffff',
  text:       '#1f2937',
  muted:      '#6b7280',
  borderTint: '#e0e7ff',
}

/* ─── Micro SVG icons (all-inline, vector-crisp at any print dpi) ─── */
const Star = ({ size = 12, color = C.amber }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill={color}
    style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    aria-hidden="true"
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77 5.82 21l1.18-6.86L2 9.27l6.91-1.01L12 2z" />
  </svg>
)

const CompassIcon = ({ size = 13 }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" fill="none" stroke={C.violet} strokeWidth="1.8" />
    <polygon points="16.2,7.8 13.9,13.9 7.8,16.2 10.1,10.1" fill={C.violet} />
    <circle cx="12" cy="12" r="1.4" fill={C.white} />
  </svg>
)

const TrophyIcon = ({ size = 13 }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none"
    style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    aria-hidden="true"
  >
    <path
      d="M6 2h12v8a6 6 0 0 1-12 0V2z"
      stroke={C.amber} strokeWidth="1.8" strokeLinejoin="round"
    />
    <path d="M6 6H3a1 1 0 0 0-1 1v1a4 4 0 0 0 4 4" stroke={C.amber} strokeWidth="1.6" strokeLinecap="round" />
    <path d="M18 6h3a1 1 0 0 1 1 1v1a4 4 0 0 1-4 4"  stroke={C.amber} strokeWidth="1.6" strokeLinecap="round" />
    <line x1="12" y1="16" x2="12" y2="20" stroke={C.amber} strokeWidth="1.8" strokeLinecap="round" />
    <line x1="8"  y1="20" x2="16" y2="20" stroke={C.amber} strokeWidth="1.8" strokeLinecap="round" />
  </svg>
)

const PeopleIcon = ({ size = 13 }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    aria-hidden="true"
  >
    {/* adult */}
    <circle cx="8.5" cy="4.5" r="2.5" fill={C.indigo} />
    <path d="M4 20v-4a4.5 4.5 0 0 1 9 0v4" fill="none" stroke={C.indigo} strokeWidth="1.8" strokeLinecap="round" />
    {/* child */}
    <circle cx="17" cy="6"   r="1.8" fill={C.violet} />
    <path d="M13.5 20v-3a3.5 3.5 0 0 1 7 0v3" fill="none" stroke={C.violet} strokeWidth="1.6" strokeLinecap="round" />
  </svg>
)

/* ─── Flyer ───────────────────────────────────────────────────────── */
export default function GrannaventyretFlyer({ qrDataUrl }) {
  return (
    <>
      <style>{PRINT_CSS}</style>

      {/*
        Root div: exact A6 landscape dimensions.
        mm units render at physical size in print; on-screen they scale to
        display DPI (typically ~559 × 397 px at 96 dpi — good preview size).
      */}
      <div
        className="ga-flyer-root"
        style={{
          width: '148mm',
          height: '105mm',
          backgroundColor: C.white,
          display: 'flex',
          flexDirection: 'row',
          overflow: 'hidden',
          position: 'relative',
          boxSizing: 'border-box',
          fontFamily: "'Trebuchet MS', 'Segoe UI', system-ui, sans-serif",
        }}
      >

        {/* ── LEFT ACCENT BAR ───────────────────────────────────────── */}
        <div
          style={{
            width: '7mm',
            background: `linear-gradient(180deg, ${C.indigo} 0%, ${C.violet} 60%, #5b21b6 100%)`,
            flexShrink: 0,
            position: 'relative',
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust: 'exact',
          }}
        >
          {/* Subtle white stars along the bar — decorative only */}
          {[8, 22, 38, 55, 72].map((topMm) => (
            <div
              key={topMm}
              style={{
                position: 'absolute',
                top: `${topMm}mm`,
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              <Star size={topMm % 3 === 0 ? 6 : 5} color="rgba(255,255,255,0.30)" />
            </div>
          ))}
        </div>

        {/* ── CONTENT ROW ───────────────────────────────────────────── */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            gap: '4mm',
            padding: '5mm 5mm 8mm 5mm',  /* bottom 8mm clears the amber stripe */
          }}
        >

          {/* ── LEFT COLUMN: branding + bullets + url ─────────────── */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >

            {/* ·· Top block ·· */}
            <div>

              {/* Logo */}
              <img
                src="/icons/logo.png"
                alt="Grannäventyret"
                style={{
                  height: '14mm',
                  width: 'auto',
                  display: 'block',
                  marginBottom: '2mm',
                  WebkitPrintColorAdjust: 'exact',
                  printColorAdjust: 'exact',
                }}
              />

              {/* App name — two-tone, very heavy */}
              <div style={{ lineHeight: 1.0, marginBottom: '1.5mm' }}>
                <span style={{
                  fontSize: '22pt',
                  fontWeight: 900,
                  color: C.indigo,
                  letterSpacing: '-0.6px',
                }}>
                  Grann
                </span>
                <span style={{
                  fontSize: '22pt',
                  fontWeight: 900,
                  color: C.violet,
                  letterSpacing: '-0.6px',
                }}>
                  äventyret
                </span>
              </div>

              {/* Amber accent divider — fades to transparent so it doesn't need clipping */}
              <div style={{
                height: '2.5px',
                width: '40mm',
                background: `linear-gradient(90deg, ${C.amber} 0%, ${C.amberLight} 55%, transparent 100%)`,
                borderRadius: '2px',
                marginBottom: '2.5mm',
                WebkitPrintColorAdjust: 'exact',
                printColorAdjust: 'exact',
              }} />

              {/* Tagline */}
              <div style={{
                fontSize: '10.5pt',
                fontWeight: 700,
                color: C.indigoDark,
                lineHeight: 1.35,
              }}>
                Äventyr väntar i<br />ditt grannskap!
              </div>
            </div>

            {/* ·· Feature bullets ·· */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2mm' }}>
              {[
                { Icon: CompassIcon, label: 'Utforska ditt grannskap' },
                { Icon: TrophyIcon,  label: 'Samla XP och belöningar' },
                { Icon: PeopleIcon,  label: 'Kul för barn 3–12 år'    },
              ].map(({ Icon, label }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Icon size={13} />
                  <span style={{ fontSize: '7.5pt', color: C.text }}>{label}</span>
                </div>
              ))}
            </div>

            {/* ·· URL strip ·· */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              paddingTop: '2mm',
              borderTop: `1px solid ${C.borderTint}`,
            }}>
              {/* Amber dot — brand accent */}
              <div style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: C.amber,
                flexShrink: 0,
                WebkitPrintColorAdjust: 'exact',
                printColorAdjust: 'exact',
              }} />
              <span style={{
                fontSize: '8.5pt',
                fontWeight: 700,
                color: C.indigo,
                fontFamily: "'Courier New', Courier, monospace",
              }}>
                grannaventyret.se
              </span>
            </div>
          </div>

          {/* ── RIGHT COLUMN: QR card ─────────────────────────────── */}
          <div
            style={{
              width: '38mm',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2.5mm',
            }}
          >

            {/* QR card */}
            <div style={{
              border: `2px solid ${C.indigo}`,
              borderRadius: '3.5mm',
              padding: '2.5mm',
              backgroundColor: C.white,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1.5mm',
              width: '100%',
              boxSizing: 'border-box',
              WebkitPrintColorAdjust: 'exact',
              printColorAdjust: 'exact',
            }}>

              {/* "SKANNA MIG" pill */}
              <div style={{
                width: '100%',
                backgroundColor: C.indigo,
                color: C.white,
                fontSize: '5.5pt',
                fontWeight: 800,
                letterSpacing: '0.9px',
                textAlign: 'center',
                padding: '1.5px 0',
                borderRadius: '1.5mm',
                boxSizing: 'border-box',
                WebkitPrintColorAdjust: 'exact',
                printColorAdjust: 'exact',
              }}>
                SKANNA MIG
              </div>

              {/* QR image — must be black on white for reliable scanning */}
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="QR-kod till grannaventyret.se"
                  style={{
                    width: '30mm',
                    height: '30mm',
                    display: 'block',
                    // pixelated keeps the modules crisp on screen; ignored at print DPI
                    imageRendering: 'pixelated',
                  }}
                />
              ) : (
                /* Placeholder shown while QR generates */
                <div style={{
                  width: '30mm',
                  height: '30mm',
                  border: `1.5px dashed #c4b5fd`,
                  borderRadius: '2mm',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <span style={{ fontSize: '5.5pt', color: C.muted, textAlign: 'center' }}>
                    QR-kod<br />laddas…
                  </span>
                </div>
              )}

              {/* URL repeated under QR — helpful if QR scan fails */}
              <span style={{
                fontSize: '5.5pt',
                color: C.muted,
                fontFamily: "'Courier New', Courier, monospace",
                textAlign: 'center',
              }}>
                grannaventyret.se
              </span>
            </div>

            {/* Decorative stars under card */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <Star size={7}  color={C.amber}  />
              <Star size={9}  color={C.violet} />
              <Star size={7}  color={C.amber}  />
            </div>
          </div>
        </div>

        {/* ── BOTTOM AMBER STRIPE ───────────────────────────────────── */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: '7mm',   /* starts where the indigo bar ends */
            right: 0,
            height: '3mm',
            background: `linear-gradient(90deg, ${C.amber} 0%, ${C.amberLight} 45%, ${C.amber} 100%)`,
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust: 'exact',
          }}
        />

        {/* ── DECORATIVE BG CIRCLE (top-right, very faint) ──────────── */}
        {/*
          Purely visual — adds slight depth on screen.
          Very low opacity; safe on inkjet (near-zero ink).
        */}
        <div
          style={{
            position: 'absolute',
            top: '-14mm',
            right: '-10mm',
            width: '32mm',
            height: '32mm',
            borderRadius: '50%',
            backgroundColor: C.violet50,
            opacity: 0.55,
            pointerEvents: 'none',
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust: 'exact',
          }}
        />

        {/* ── DECORATIVE BG CIRCLE (bottom-left content area, faint) ── */}
        <div
          style={{
            position: 'absolute',
            bottom: '5mm',
            left: '20mm',
            width: '18mm',
            height: '18mm',
            borderRadius: '50%',
            backgroundColor: C.indigo50,
            opacity: 0.4,
            pointerEvents: 'none',
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust: 'exact',
          }}
        />
      </div>
    </>
  )
}
